import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Tractor, Wrench } from "lucide-react";

const TASK_TYPES = ["labour", "préparation_sol", "semis", "désherbage", "fertilisation", "traitement", "irrigation", "récolte", "entretien"];
const TASK_STATUS = ["planifiée", "en_cours", "terminée"];

export default function Travaux() {
  const { t, lang } = useI18n();
  const [tasks, setTasks] = useState([]);
  const [parcelles, setParcelles] = useState([]);
  const [open, setOpen] = useState(false);
  const [newT, setNewT] = useState({ type: "labour", parcelle_id: "", assignee: "", date: "", equipment: "", cost: "", status: "planifiée" });

  async function load() {
    const [tk, p] = await Promise.all([api.get("/tasks"), api.get("/parcelles")]);
    setTasks(tk.data.sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    setParcelles(p.data);
  }
  useEffect(() => { load(); }, []);

  // Pré-remplissage via l'assistant IA (action create_task)
  useEffect(() => {
    const raw = sessionStorage.getItem("ai_prefill");
    if (!raw || parcelles.length === 0) return;
    try {
      const a = JSON.parse(raw);
      if (a.type === "create_task") {
        const par = parcelles.find((p) => (p.name || "").toLowerCase() === (a.parcelle_name || "").toLowerCase());
        setNewT((prev) => ({
          ...prev,
          type: a.task_type || prev.type,
          parcelle_id: par?.id || "",
          date: a.date || "",
          cost: a.cost ? String(a.cost) : "",
          assignee: a.assignee || "",
          equipment: a.equipment || "",
        }));
        setOpen(true);
        toast.success(t("ai_prefilled"));
      }
    } catch { /* ignore */ }
    sessionStorage.removeItem("ai_prefill");
  }, [parcelles]);

  async function addTask() {
    if (!newT.parcelle_id) return toast.error("Parcelle requise");
    const par = parcelles.find((p) => p.id === newT.parcelle_id);
    try {
      await api.post("/tasks", {
        ...newT, parcelle_name: par?.name,
        cost: parseFloat(newT.cost || 0),
        date: newT.date ? new Date(newT.date).toISOString() : new Date().toISOString(),
      });
      setOpen(false); setNewT({ type: "labour", parcelle_id: "", assignee: "", date: "", equipment: "", cost: "", status: "planifiée" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function setStatus(id, status) {
    try {
      await api.put(`/tasks/${id}`, { status });
      toast.success(t("updated")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delTask(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/tasks/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  const totalCost = tasks.reduce((s, tk) => s + (tk.cost || 0), 0);

  return (
    <div className="space-y-6" data-testid="travaux-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">{t("agri_kpis")}</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("tasks")}</h1>
          <p className="text-muted-foreground mt-1">{tasks.length} {t("tasks").toLowerCase()} · {formatXAF(totalCost)}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" />{t("tasks")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("tasks")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("type")}</Label>
                <Select value={newT.type} onValueChange={(v) => setNewT({ ...newT, type: v })}>
                  <SelectTrigger data-testid="task-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TASK_TYPES.map((x) => <SelectItem key={x} value={x}>{t(`task_${x}`) || x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("parcelle")}</Label>
                <Select value={newT.parcelle_id} onValueChange={(v) => setNewT({ ...newT, parcelle_id: v })}>
                  <SelectTrigger data-testid="task-parcelle"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{parcelles.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("assignee")}</Label><Input data-testid="task-assignee" value={newT.assignee} onChange={(e) => setNewT({ ...newT, assignee: e.target.value })} /></div>
              <div><Label>{t("date")}</Label><Input type="date" value={newT.date} onChange={(e) => setNewT({ ...newT, date: e.target.value })} /></div>
              <div><Label>{t("equipment")}</Label><Input data-testid="task-equipment" value={newT.equipment} onChange={(e) => setNewT({ ...newT, equipment: e.target.value })} /></div>
              <div><Label>{t("cost")} (FCFA)</Label><Input type="number" data-testid="task-cost" value={newT.cost} onChange={(e) => setNewT({ ...newT, cost: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addTask} data-testid="task-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border bg-secondary/50">
              <tr>
                <th className="p-3">{t("type")}</th>
                <th>{t("parcelle")}</th>
                <th>{t("assignee")}</th>
                <th>{t("date")}</th>
                <th>{t("equipment")}</th>
                <th>{t("cost")}</th>
                <th>{t("status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((tk) => (
                <tr key={tk.id} className="border-b border-border/60 last:border-0" data-testid={`task-row-${tk.id}`}>
                  <td className="p-3 font-medium flex items-center gap-2">
                    {tk.type === "labour" ? <Tractor className="w-4 h-4 text-primary" /> : <Wrench className="w-4 h-4 text-muted-foreground" />}
                    {t(`task_${tk.type}`) || tk.type}
                  </td>
                  <td>{tk.parcelle_name}</td>
                  <td>{tk.assignee || "—"}</td>
                  <td>{formatDate(tk.date, lang)}</td>
                  <td>{tk.equipment || "—"}</td>
                  <td className="font-semibold">{formatXAF(tk.cost)}</td>
                  <td>
                    <Select value={tk.status} onValueChange={(v) => setStatus(tk.id, v)}>
                      <SelectTrigger className="h-8 w-32" data-testid={`task-status-${tk.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_STATUS.map((s) => <SelectItem key={s} value={s}>{t(s) || s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td><Button variant="ghost" size="sm" onClick={() => delTask(tk.id)}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
              {tasks.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-sm text-muted-foreground">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
